import axiosInstance from '@/lib/axios';
import { handleApiError } from '@/lib/api-error-handler';
import type {
  LoginRequest,
  RegisterRequest,
  GoogleAuthRequest,
  RefreshTokenRequest,
  AuthResponse,
  SelectAccountRequest,
} from '@/types/auth';

class AuthApi {
  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        '/auth/login',
        credentials
      );
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        '/auth/register',
        data
      );
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Login with Google OAuth
   */
  async loginWithGoogle(data: GoogleAuthRequest): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        '/auth/google',
        data
      );
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(data: RefreshTokenRequest): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        '/auth/refresh',
        data
      );
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Select account for multi-account login
   */
  async selectAccount(data: SelectAccountRequest): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        '/auth/select-account',
        data
      );
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Logout user and invalidate tokens
   */
  async logout(): Promise<void> {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Get current user data
   */
  async getCurrentUser(): Promise<any> {
    try {
      // Add cache busting to always get fresh data
      const response = await axiosInstance.get('/auth/me', {
        params: {
          _t: Date.now(),
          _r: Math.random()
        }
      });
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Get current user data fresh from database (no cache)
   * Use this for critical operations like plan limit checks
   */
  async getCurrentUserFresh(): Promise<any> {
    try {
      const response = await axiosInstance.get('/auth/me/fresh');
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    try {
      await axiosInstance.get(`/auth/verify-email?token=${token}`);
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await axiosInstance.post('/auth/forgot-password', { email });
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Validate reset token and get email
   */
  async validateResetToken(token: string): Promise<string> {
    try {
      const response = await axiosInstance.post<{ email: string }>('/auth/validate-reset-token', { token });
      return response.data.email;
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await axiosInstance.post('/auth/reset-password', { token, newPassword });
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Send verification token (6 digits) using reset token
   */
  async sendVerificationToken(token: string): Promise<void> {
    try {
      await axiosInstance.post('/auth/send-verification-token', { token });
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Verify reset token
   */
  async verifyResetToken(email: string, token: string): Promise<void> {
    try {
      await axiosInstance.post('/auth/verify-reset-token', { email, token });
    } catch (error: any) {
      throw handleApiError(error);
    }
  }

  /**
   * Reset password with 6-digit token
   */
  async resetPasswordWithToken(email: string, token: string, newPassword: string): Promise<void> {
    try {
      await axiosInstance.post('/auth/reset-password-with-token', { 
        email, 
        token, 
        newPassword 
      });
    } catch (error: any) {
      throw handleApiError(error);
    }
  }
}

export const authApi = new AuthApi();
